import { useEffect, useState } from 'react';
import { API_BASE } from '../lib/api';

/** M7 — Migration Progress dashboard (first tab). Reads /api/migration/dashboard,
 *  which recomputes from the synced roster, so it tracks each source sync. */

function Bar({ pct, color }: { pct: number; color: string }) {
  return (
    <div className="h-2 rounded bg-gray-200 dark:bg-gray-700 overflow-hidden">
      <div className={`h-full ${color}`} style={{ width: Math.min(100, Math.max(0, pct)) + '%' }} />
    </div>
  );
}

function ProgCard({ d, color }: { d: any; color: string }) {
  const num = d.migrated ?? d.used ?? 0;
  const den = d.total ?? d.pool ?? 0;
  return (
    <div className="card p-3">
      <div className="text-xs text-gray-500">{d.label}</div>
      <div className="text-2xl font-semibold mt-1">
        {num.toLocaleString()}<span className="text-sm text-gray-400"> / {den.toLocaleString()}</span>
      </div>
      <div className="mt-2"><Bar pct={d.pct} color={color} /></div>
      <div className="text-xs text-gray-400 mt-1">{d.pct}%{den === 0 ? ' · set pool below' : ''}</div>
    </div>
  );
}

function MiniChart({ d }: { d: any }) {
  const items = [
    { name: 'Users', m: d.users.migrated, t: d.users.total },
    { name: 'Phones', m: d.phones.migrated, t: d.phones.total },
    { name: 'Contact Ctr', m: d.contactCenter.migrated, t: d.contactCenter.total },
  ];
  const max = Math.max(1, ...items.map((i) => i.t));
  const bw = 64, gap = 44, base = 108;
  return (
    <div className="card p-3">
      <div className="text-sm font-semibold mb-2">Migration progress by domain</div>
      <svg viewBox="0 0 340 130" className="w-full" style={{ maxHeight: 190 }} role="img" aria-label="Migration progress by domain">
        {items.map((it, i) => {
          const x = 26 + i * (bw + gap);
          const th = (it.t / max) * 88;
          const mh = (it.m / max) * 88;
          const p = Math.round((it.m / (it.t || 1)) * 100);
          return (
            <g key={i}>
              <rect x={x} y={base - th} width={bw} height={th} fill="#e5e7eb" rx="3" />
              <rect x={x} y={base - mh} width={bw} height={mh} fill="#0063C1" rx="3" />
              <text x={x + bw / 2} y={base + 14} textAnchor="middle" fontSize="10" fill="#6b7280">{it.name}</text>
              <text x={x + bw / 2} y={base - mh - 4} textAnchor="middle" fontSize="10" fill="#0063C1">{p}%</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function DashboardTab({ canEdit }: { canEdit: boolean }) {
  const [d, setD] = useState<any>(null);
  const [pool, setPool] = useState<any>({ wxc: 0, wxcc: 0 });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/api/migration/dashboard`, { credentials: 'include' })
      .then((r) => r.json()).then((j) => { setD(j.dashboard); setPool(j.pool || { wxc: 0, wxcc: 0 }); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);
  const savePool = () => {
    setMsg('Saving…');
    fetch(`${API_BASE}/api/migration/dashboard/license-pool`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ wxc: Number(pool.wxc) || 0, wxcc: Number(pool.wxcc) || 0 }) })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('save')))).then(() => { setMsg('Saved'); load(); }).catch(() => setMsg('Error'));
  };

  if (!d) return <div className="text-sm text-gray-500">{loading ? 'Loading dashboard…' : 'No data'}</div>;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-gray-500">Migration progress · updates with each source sync · <button className="text-tnh-accent hover:underline" onClick={load}>{loading ? 'refreshing…' : 'refresh'}</button></div>
        <div className="text-xs text-gray-400">{(d.endUsers?.total || 0).toLocaleString()} end users in scope</div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <ProgCard d={d.users} color="bg-blue-500" />
        <ProgCard d={d.phones} color="bg-indigo-500" />
        <ProgCard d={d.contactCenter} color="bg-emerald-500" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <ProgCard d={d.license.wxc} color="bg-amber-500" />
        <ProgCard d={d.license.wxcc} color="bg-rose-500" />
      </div>
      <MiniChart d={d} />
      <div className="card p-3">
        <div className="text-sm font-semibold mb-2">End users by verdict</div>
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(d.endUsers?.byVerdict || {}).map(([k, v]: any) => (
            <span key={k} className="px-2 py-1 rounded bg-tnh-accent/10 text-tnh-accent">{k}: {Number(v).toLocaleString()}</span>
          ))}
        </div>
      </div>
      {canEdit && (
        <div className="card p-3">
          <div className="text-sm font-semibold mb-2">License pool <span className="font-normal text-gray-400">— tracked in TriNetHub; Cisco provisions the actual licenses</span></div>
          <div className="flex gap-3 items-end flex-wrap">
            <label className="text-xs text-gray-500">WxC pool<input className="input-sm block mt-1 w-28" type="number" value={pool.wxc} onChange={(e) => setPool({ ...pool, wxc: e.target.value })} /></label>
            <label className="text-xs text-gray-500">WxCC pool<input className="input-sm block mt-1 w-28" type="number" value={pool.wxcc} onChange={(e) => setPool({ ...pool, wxcc: e.target.value })} /></label>
            <button className="btn-secondary" onClick={savePool}>Save pool</button>
            {msg && <span className="text-xs text-gray-500">{msg}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
